import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { TypePill } from '@/components/synapse/TypePill';
import type { IconName } from '@/components/synapse/Icon';
import { ScanDialog } from '@/components/synapse/ScanDialog';
import { BatchRow } from '@/components/synapse/BatchRow';
import { ItemSearchField } from '@/components/synapse/ItemSearchField';
import { isPastDate } from '@/lib/expiry';
import {
  useExpiringBatches,
  useItemByBarcode,
  useItemDetail,
  useStockAdjust,
} from '@/hooks/useInventory';
import type {
  AdjustmentEntry,
  AdjustmentReason,
  BatchWithBalance,
  ExpiringBatch,
  Item,
  ItemDetail,
  ItemType,
} from '@/types/api';

// What the draft editor actually displays — a strict subset of Item plus the
// fields ExpiringBatch can supply. Avoids fabricating server-owned fields like
// `clinicId` / timestamps when seeding a draft from the expiring-batches list.
type DraftItem = Pick<Item, 'id' | 'type' | 'brandName' | 'genericName' | 'strength'>;
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';

const TYPE_ICON: Record<ItemType, IconName> = {
  medication: 'pill',
  consumable: 'box',
  medical_device: 'device',
  reagent: 'flask',
  other: 'box_in',
};

const REASONS: { value: AdjustmentReason; labelKey: string }[] = [
  { value: 'expired', labelKey: 'adjust.reasonExpired' },
  { value: 'damaged', labelKey: 'adjust.reasonDamaged' },
  { value: 'discarded', labelKey: 'adjust.reasonDiscarded' },
  { value: 'other', labelKey: 'adjust.reasonOther' },
];

const REASON_LABEL_KEY: Record<AdjustmentReason, string> = {
  expired: 'adjust.reasonExpired',
  damaged: 'adjust.reasonDamaged',
  discarded: 'adjust.reasonDiscarded',
  other: 'adjust.reasonOther',
};

interface AdjustRow {
  rowId: string;
  itemId: string;
  type: ItemType;
  brandName: string;
  genericName: string | null;
  strength: string | null;
  batchId: string;
  batchNumber: string | null;
  expiryDate: string | null;
  batchBalance: number;
  quantity: number;
  reason: AdjustmentReason;
  note: string | null;
}

interface DraftSelection {
  item: DraftItem;
  /** Explicit selection — never auto-attached, even for single-batch items.
   *  Adjustment is a deliberate action and one extra tap is appropriate. */
  batch: BatchWithBalance | null;
  reason: AdjustmentReason | null;
  quantity: number;
  note: string;
}

function rowToEntry(row: AdjustRow): AdjustmentEntry {
  return {
    batchId: row.batchId,
    quantity: -row.quantity,
    reason: row.reason,
    note: row.note,
  };
}

export function AdjustPage() {
  const { t } = useTranslation();
  const [pending, setPending] = useState<AdjustRow[]>([]);
  const [draft, setDraft] = useState<DraftSelection | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitted, setSubmitted] = useState<{ count: number; units: number } | null>(null);
  const adjust = useStockAdjust();
  const { data: expiring = [] } = useExpiringBatches({ withinDays: 30, limit: 50 });

  const itemId = draft?.item.id ?? null;
  const { data: detail, isLoading: detailLoading } = useItemDetail(itemId, {
    enabled: itemId !== null,
  });

  const totals = useMemo(
    () => ({
      count: pending.length,
      units: pending.reduce((acc, r) => acc + r.quantity, 0),
    }),
    [pending],
  );

  const quantityValid = useMemo(() => {
    if (!draft || !draft.batch) return false;
    if (!Number.isInteger(draft.quantity) || draft.quantity <= 0) return false;
    return draft.quantity <= draft.batch.balance;
  }, [draft]);

  const draftValid = useMemo(
    () => Boolean(draft && draft.batch && draft.reason && quantityValid),
    [draft, quantityValid],
  );

  const onPickItem = (item: Item) => {
    if (item.totalStock <= 0) return;
    setDraft({
      item: {
        id: item.id,
        type: item.type,
        brandName: item.brandName,
        genericName: item.genericName,
        strength: item.strength,
      },
      batch: null,
      reason: null,
      quantity: 1,
      note: '',
    });
  };

  // When the item-detail query resolves, re-sync `draft.batch` against the
  // server's current balance and clamp `quantity` to it. This matters when the
  // draft was seeded from `useExpiringBatches` — that snapshot can be stale by
  // the time the user finishes filling the form, and we'd rather catch oversell
  // here than on commit.
  useEffect(() => {
    if (!draft || !detail || draft.item.id !== detail.id || !draft.batch) return;
    const fresh = detail.batches.find((b) => b.id === draft.batch!.id);
    if (!fresh) return;
    if (fresh.balance === draft.batch.balance) return;
    setDraft((d) =>
      d && d.batch
        ? {
            ...d,
            batch: fresh,
            quantity: Math.min(d.quantity, Math.max(1, fresh.balance)),
          }
        : d,
    );
  }, [detail, draft]);

  const onPickBatch = (batch: BatchWithBalance) => {
    setDraft((d) =>
      d ? { ...d, batch, quantity: Math.min(d.quantity, batch.balance) } : d,
    );
  };

  const onPickExpiring = (eb: ExpiringBatch) => {
    setDraft({
      item: {
        id: eb.itemId,
        type: eb.type,
        brandName: eb.brandName,
        genericName: eb.genericName,
        strength: eb.strength,
      },
      batch: {
        id: eb.id,
        itemId: eb.itemId,
        batchNumber: eb.batchNumber,
        expiryDate: eb.expiryDate,
        createdAt: '',
        balance: eb.balance,
      },
      // Pre-fill expired/expiring reason — staff can change it.
      reason: isPastDate(eb.expiryDate) ? 'expired' : null,
      quantity: eb.balance,
      note: '',
    });
  };

  const addRow = () => {
    if (!draft || !draft.batch || !draft.reason || !quantityValid) return;
    setPending((prev) => [
      ...prev,
      {
        rowId: crypto.randomUUID(),
        itemId: draft.item.id,
        type: draft.item.type,
        brandName: draft.item.brandName,
        genericName: draft.item.genericName,
        strength: draft.item.strength,
        batchId: draft.batch!.id,
        batchNumber: draft.batch!.batchNumber,
        expiryDate: draft.batch!.expiryDate,
        batchBalance: draft.batch!.balance,
        quantity: draft.quantity,
        reason: draft.reason!,
        note: draft.note.trim() || null,
      },
    ]);
    setDraft(null);
  };

  const removeRow = (rowId: string) => {
    setPending((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  const onScanResolved = (item: ItemDetail) => {
    setScanOpen(false);
    if (item.totalStock <= 0) return;
    setDraft({
      item: {
        id: item.id,
        type: item.type,
        brandName: item.brandName,
        genericName: item.genericName,
        strength: item.strength,
      },
      // Do not auto-attach — even with one batch the user confirms by picking it.
      batch: null,
      reason: null,
      quantity: 1,
      note: '',
    });
  };

  const onCommit = async () => {
    if (pending.length === 0) return;
    try {
      await adjust.mutateAsync({ entries: pending.map(rowToEntry) });
      setSubmitted({ count: totals.count, units: totals.units });
      setPending([]);
      setDraft(null);
      setConfirmOpen(false);
    } catch {
      // ApiError captured to adjust.error; rendered inline in the confirm dialog.
    }
  };

  if (submitted) {
    return (
      <SubmitSuccess
        count={submitted.count}
        units={submitted.units}
        onStartAnother={() => setSubmitted(null)}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-col gap-1.5 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">{t('adjust.title')}</h1>
        <p className="text-[13.5px] text-muted">{t('adjust.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.05fr] lg:items-start lg:gap-6">
        {/* LEFT — entry */}
        <div className="flex flex-col gap-3">
          {expiring.length > 0 && !draft && (
            <ExpiringList items={expiring} onPick={onPickExpiring} />
          )}

          <Card className="p-0">
            <CardContent className="p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
                <button
                  type="button"
                  onClick={() => setScanOpen(true)}
                  className="flex h-[88px] flex-1 items-center gap-4 rounded-lg bg-accent px-5 text-left text-accent-foreground transition-opacity hover:opacity-90"
                >
                  <span className="flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-white/10">
                    <Icon name="scan" size={24} />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[16px] font-semibold tracking-[-0.02em]">
                      {t('adjust.scanBarcode')}
                    </span>
                    <span className="mt-0.5 text-xs opacity-75">{t('adjust.scanHint')}</span>
                  </span>
                </button>
                <div className="hidden w-[200px] flex-col justify-center gap-1 rounded-lg border border-dashed border-line-2 px-4 py-3.5 sm:flex">
                  <div className="text-[13px] font-semibold">{t('adjust.orSearch')}</div>
                  <div className="text-[11.5px] text-muted">{t('adjust.orSearchHint')}</div>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3.5">
                <ItemSearchField onPick={onPickItem} disabledItemId={draft?.item.id ?? null} />

                {draft && (
                  <DraftEditor
                    draft={draft}
                    detail={detail}
                    detailLoading={detailLoading}
                    draftValid={draftValid}
                    onPickBatch={onPickBatch}
                    onPickReason={(reason) => setDraft((d) => (d ? { ...d, reason } : d))}
                    onChangeQuantity={(q) =>
                      setDraft((d) => (d ? { ...d, quantity: q } : d))
                    }
                    onChangeNote={(note) =>
                      setDraft((d) => (d ? { ...d, note } : d))
                    }
                    onUseWholeBatch={() =>
                      setDraft((d) =>
                        d && d.batch ? { ...d, quantity: d.batch.balance } : d,
                      )
                    }
                    onCancel={() => setDraft(null)}
                    onAdd={addRow}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — running list */}
        <div className="lg:sticky lg:top-4">
          <Card className="overflow-hidden p-0">
            <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
              <div className="flex-1">
                <div className="text-[14px] font-semibold tracking-[-0.01em]">
                  {t('adjust.adjustmentsTitle')}
                </div>
                <div className="mt-0.5 text-[12px] text-muted tabular-nums">
                  {totals.count === 0
                    ? t('adjust.noAdjustmentsYet')
                    : t('adjust.linesAndUnits', {
                        count: totals.count,
                        lineLabel: t('common.line', { count: totals.count }),
                        units: totals.units,
                        unitLabel: t('common.unit', { count: totals.units }),
                      })}
                </div>
              </div>
              {pending.length > 0 && (
                <span className="inline-flex h-[22px] items-center rounded-full bg-warn-bg px-2 text-[11.5px] font-medium text-warn">
                  {t('common.pending')}
                </span>
              )}
            </div>

            <div className="max-h-[540px] overflow-auto px-3.5 py-3.5">
              {pending.length === 0 ? (
                <EmptyList />
              ) : (
                <ul className="flex flex-col gap-2">
                  {pending.map((row) => (
                    <li key={row.rowId}>
                      <AdjustListRow row={row} onRemove={removeRow} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3.5">
              <div className="text-[12.5px] text-muted">
                {pending.length === 0
                  ? t('adjust.addAtLeastOne')
                  : t('adjust.reviewHint')}
              </div>
              <Button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={pending.length === 0 || adjust.isPending}
              >
                <Icon name="check" size={16} />
                {t('adjust.reviewBtn')}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {scanOpen && (
        <AdjustScanDialog
          onClose={() => setScanOpen(false)}
          onResolved={onScanResolved}
        />
      )}

      {confirmOpen && (
        <ConfirmDialog
          rows={pending}
          isPending={adjust.isPending}
          error={adjust.isError ? humaniseAdjustError(adjust.error, t) : null}
          onClose={() => setConfirmOpen(false)}
          onCommit={onCommit}
        />
      )}
    </div>
  );
}

function humaniseAdjustError(err: unknown, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (err instanceof ApiError) {
    if (err.code === 'INSUFFICIENT_STOCK') {
      return t('adjust.errInsufficient', { message: translateApiError(err) });
    }
    if (err.code === 'CONFLICT') return translateApiError(err);
    if (err.code === 'VALIDATION_ERROR') return t('adjust.errInvalidLines');
    return translateApiError(err);
  }
  return t('adjust.errCommitFailed');
}

// ---------------- subcomponents ----------------

function EmptyList() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="adjust" size={22} />
      </div>
      <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{t('adjust.nothingToAdjust')}</div>
      <div className="max-w-[260px] text-[12.5px] text-muted">{t('adjust.nothingToAdjustBody')}</div>
    </div>
  );
}

interface ExpiringListProps {
  items: ExpiringBatch[];
  onPick: (eb: ExpiringBatch) => void;
}

function ExpiringList({ items, onPick }: ExpiringListProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  return (
    <Card className="overflow-hidden p-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 border-b border-line px-4 py-3 text-left hover:bg-paper-2"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-warn-bg text-warn">
            <Icon name="alert" size={16} />
          </span>
          <div>
            <div className="text-[13.5px] font-semibold tracking-[-0.01em]">
              {t('adjust.expiringHeading')}
            </div>
            <div className="text-[11.5px] text-muted">
              {t('adjust.expiringSubtitle', {
                count: items.length,
                batchLabel: t('common.batch', { count: items.length }),
              })}
            </div>
          </div>
        </div>
        <Icon name={open ? 'chevron_up' : 'chevron_down'} size={16} className="text-muted" />
      </button>
      {open && (
        <ul className="max-h-[260px] divide-y divide-line overflow-auto">
          {items.map((b) => {
            const past = isPastDate(b.expiryDate);
            return (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => onPick(b)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-paper-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[13.5px] font-semibold tracking-[-0.01em]">
                        {b.brandName}
                      </span>
                      {b.strength && (
                        <span className="font-mono text-[12.5px] text-ink-2 tabular-nums">
                          · {b.strength}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11.5px] text-muted">
                      <TypePill type={b.type} size="sm" />
                      <span className="font-mono">{b.batchNumber ?? t('issue.batchDash')}</span>
                      <span
                        className={cn(
                          'font-mono',
                          past ? 'text-destructive' : 'text-warn',
                        )}
                      >
                        {past ? t('adjust.expiredOn', { date: b.expiryDate }) : t('adjust.expDate', { date: b.expiryDate })}
                      </span>
                      <span className="font-mono tabular-nums">{t('issue.nOnHand', { count: b.balance })}</span>
                    </div>
                  </div>
                  <Icon name="chevron_right" size={14} className="text-muted" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

interface DraftEditorProps {
  draft: DraftSelection;
  detail: ItemDetail | undefined;
  detailLoading: boolean;
  draftValid: boolean;
  onPickBatch: (batch: BatchWithBalance) => void;
  onPickReason: (reason: AdjustmentReason) => void;
  onChangeQuantity: (q: number) => void;
  onChangeNote: (note: string) => void;
  onUseWholeBatch: () => void;
  onCancel: () => void;
  onAdd: () => void;
}

function DraftEditor({
  draft,
  detail,
  detailLoading,
  draftValid,
  onPickBatch,
  onPickReason,
  onChangeQuantity,
  onChangeNote,
  onUseWholeBatch,
  onCancel,
  onAdd,
}: DraftEditorProps) {
  const { t } = useTranslation();
  // When the draft was seeded from the expiring list it already has a batch;
  // detail may still be loading or empty. Prefer the draft.batch in that case.
  const stockedBatches = useMemo(() => {
    const fromDetail = (detail?.batches ?? []).filter((b) => b.balance > 0);
    if (fromDetail.length > 0) return fromDetail;
    return draft.batch ? [draft.batch] : [];
  }, [detail, draft.batch]);

  return (
    <div className="rounded-md border border-line bg-paper-2 p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[14px] font-semibold tracking-[-0.01em]">
              {draft.item.brandName}
            </span>
            <span className="text-[13px] text-muted">{draft.item.genericName}</span>
            {draft.item.strength && (
              <span className="font-mono text-[13px] text-ink-2 tabular-nums">
                · {draft.item.strength}
              </span>
            )}
          </div>
          <div className="mt-1.5">
            <TypePill type={draft.item.type} size="sm" />
          </div>
        </div>
        <button
          type="button"
          aria-label={t('issue.cancelSelection')}
          onClick={onCancel}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink"
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      <div className="mt-3">
        <Field
          label={t('common.batch')}
          hint={
            detailLoading
              ? t('issue.batchLoadingHint')
              : stockedBatches.length === 1
              ? t('adjust.confirmSingleBatchHint')
              : t('adjust.pickBatchHint')
          }
        >
          {detailLoading && stockedBatches.length === 0 ? (
            <div className="h-10 animate-pulse rounded-md bg-paper" />
          ) : stockedBatches.length === 0 ? (
            <div className="rounded-md border border-line bg-paper px-3 py-2 text-[12.5px] text-destructive">
              {t('issue.noStockedBatches')}
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {stockedBatches.map((b) => {
                const active = draft.batch?.id === b.id;
                return (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => onPickBatch(b)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors',
                        active
                          ? 'border-accent bg-paper'
                          : 'border-line bg-paper hover:bg-paper-2',
                      )}
                    >
                      <BatchSummary batch={b} />
                      {active && (
                        <Icon name="check" size={14} className="ml-auto text-success" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Field>
      </div>

      <div className="mt-3">
        <Field label={t('common.reason')} hint={t('adjust.reasonHint')}>
          <div role="radiogroup" aria-label={t('common.reason')} className="flex flex-wrap gap-1.5">
            {REASONS.map((r) => {
              const on = draft.reason === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onPickReason(r.value)}
                  className={cn(
                    'h-9 rounded-md border px-3 text-[12.5px] tracking-[-0.01em] transition-colors',
                    on
                      ? 'border-accent bg-paper font-semibold text-ink'
                      : 'border-line bg-paper font-medium text-muted hover:text-ink-2',
                  )}
                >
                  {t(r.labelKey)}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px]">
        <Field label={t('common.note')} optional>
          <Input
            value={draft.note}
            onChange={(e) => onChangeNote(e.target.value)}
            placeholder={t('adjust.notePlaceholder')}
          />
        </Field>

        <Field
          label={t('common.quantity')}
          hint={draft.batch ? t('issue.upToBalance', { balance: draft.batch.balance }) : undefined}
        >
          <div className="flex gap-1.5">
            <button
              type="button"
              aria-label={t('common.decreaseQuantity')}
              onClick={() => onChangeQuantity(Math.max(1, draft.quantity - 1))}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
            >
              <Icon name="minus" size={16} />
            </button>
            <Input
              mono
              className="flex-1 text-center"
              type="number"
              min={1}
              max={draft.batch?.balance}
              value={draft.quantity}
              onChange={(e) =>
                onChangeQuantity(Math.max(1, Number(e.target.value) || 0))
              }
            />
            <button
              type="button"
              aria-label={t('common.increaseQuantity')}
              onClick={() =>
                onChangeQuantity(
                  draft.batch
                    ? Math.min(draft.batch.balance, draft.quantity + 1)
                    : draft.quantity + 1,
                )
              }
              className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
            >
              <Icon name="plus" size={16} />
            </button>
          </div>
        </Field>
      </div>

      {draft.batch && (
        <div className="mt-2">
          <button
            type="button"
            onClick={onUseWholeBatch}
            className="text-[12px] text-ink-2 underline-offset-2 hover:text-ink hover:underline"
          >
            {t('adjust.wholeBatch', {
              count: draft.batch.balance,
              unitLabel: t('common.unit', { count: draft.batch.balance }),
            })}
          </button>
        </div>
      )}

      {draft.batch && draft.quantity > draft.batch.balance && (
        <div className="mt-2 text-[12px] text-destructive">
          {t('adjust.onlyAvailable', { balance: draft.batch.balance })}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={onAdd} disabled={!draftValid}>
          <Icon name="plus" size={16} /> {t('adjust.addAdjustment')}
        </Button>
      </div>
    </div>
  );
}

function BatchSummary({ batch }: { batch: BatchWithBalance }) {
  // Adjust uses the same narrower threshold (30) as the expiring-batches feed
  // it pairs with, so the warn coloring matches what the user just clicked on.
  return <BatchRow batch={batch} withinDays={30} />;
}

interface AdjustListRowProps {
  row: AdjustRow;
  onRemove: (rowId: string) => void;
}

function AdjustListRow({ row, onRemove }: AdjustListRowProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 rounded-md border border-line bg-paper p-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper-2 text-muted">
        <Icon name={TYPE_ICON[row.type]} size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-semibold tracking-[-0.01em]">{row.brandName}</span>
          <span className="text-[13px] text-muted">{row.genericName}</span>
          {row.strength && (
            <span className="font-mono text-[13px] text-ink-2 tabular-nums">
              · {row.strength}
            </span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <TypePill type={row.type} size="sm" />
          <span className="font-mono text-[11.5px] text-muted">
            {row.batchNumber ?? t('issue.batchDash')}
          </span>
          {row.expiryDate && (
            <span
              className={cn(
                'font-mono text-[11.5px]',
                isPastDate(row.expiryDate) ? 'text-destructive' : 'text-muted',
              )}
            >
              {isPastDate(row.expiryDate)
                ? t('adjust.expiredOn', { date: row.expiryDate })
                : t('adjust.expDate', { date: row.expiryDate })}
            </span>
          )}
          <span className="inline-flex h-[18px] items-center rounded-full bg-warn-bg px-1.5 text-[10.5px] font-medium text-warn">
            {t(REASON_LABEL_KEY[row.reason])}
          </span>
        </div>
        {row.note && (
          <div className="mt-1 truncate text-[12px] text-muted">{row.note}</div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <span className="font-mono text-[15px] font-semibold tabular-nums text-ink">
          −{row.quantity}
        </span>
        <button
          type="button"
          aria-label={`${t('common.remove')} ${row.brandName}`}
          onClick={() => onRemove(row.rowId)}
          className="flex h-7 w-7 items-center justify-center rounded-md border border-line bg-paper text-muted hover:text-destructive"
        >
          <Icon name="trash" size={13} />
        </button>
      </div>
    </div>
  );
}

interface AdjustScanDialogProps {
  onClose: () => void;
  onResolved: (item: ItemDetail) => void;
}

function AdjustScanDialog({ onClose, onResolved }: AdjustScanDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const { data: match, isFetching, error } = useItemByBarcode(code, {
    enabled: code.trim().length >= 3,
  });

  const oos = match ? match.totalStock <= 0 : false;
  const canUse = match !== undefined && match !== null && !oos;

  return (
    <ScanDialog
      onClose={onClose}
      onCodeChange={setCode}
      canUse={() => canUse}
      onUse={() => {
        if (match && !oos) onResolved(match);
      }}
      renderPreview={() => {
        if (isFetching) return <span className="text-muted">{t('common.lookingUp')}</span>;
        if (error instanceof ApiError && error.code === 'NOT_FOUND') {
          return <span className="text-muted">{t('issue.unknownBarcode')}</span>;
        }
        if (match && oos) {
          return (
            <span className="text-destructive">
              {t('adjust.noAvailableStockOf', { item: match.brandName })}
            </span>
          );
        }
        if (match) {
          return (
            <span className="text-success">
              <strong className="font-semibold">{match.brandName}</strong>
              {match.strength ? ` · ${match.strength}` : ''} —{' '}
              <span className="font-mono tabular-nums">{t('issue.nOnHand', { count: match.totalStock })}</span>
            </span>
          );
        }
        return null;
      }}
    />
  );
}

interface ConfirmDialogProps {
  rows: AdjustRow[];
  isPending: boolean;
  error: string | null;
  onClose: () => void;
  onCommit: () => void;
}

function ConfirmDialog({ rows, isPending, error, onClose, onCommit }: ConfirmDialogProps) {
  const { t } = useTranslation();
  const totalUnits = rows.reduce((acc, r) => acc + r.quantity, 0);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('adjust.confirmTitle')}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-t-2xl bg-paper p-5 sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[15px] font-semibold tracking-[-0.01em]">
              {t('adjust.confirmTitle')}
            </div>
            <p className="mt-1 text-[12.5px] text-muted">
              {t('adjust.confirmBody', {
                count: rows.length,
                lineLabel: t('common.line', { count: rows.length }),
                units: totalUnits,
                unitLabel: t('common.unit', { count: totalUnits }),
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            disabled={isPending}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink disabled:opacity-50"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <ul className="mt-3.5 flex max-h-[320px] flex-col gap-2 overflow-auto">
          {rows.map((r) => (
            <li
              key={r.rowId}
              className="rounded-md border border-line bg-paper-2 px-3 py-2 text-[12.5px] leading-[1.5]"
            >
              <div className="font-semibold text-ink">
                {t('adjust.removeLinePlain', {
                  quantity: r.quantity,
                  unitLabel: t('common.unit', { count: r.quantity }),
                  brand: `${r.brandName}${r.strength ? ` ${r.strength}` : ''}`,
                })}
              </div>
              <div className="mt-0.5 text-muted">
                {t('adjust.batchLabel', { number: r.batchNumber ?? '—' })}
                {r.expiryDate ? ` (${t('adjust.expDate', { date: r.expiryDate })})` : ''} —{' '}
                {t('adjust.reasonLabel', { reason: t(REASON_LABEL_KEY[r.reason]) })}
                {r.note ? ` — ${r.note}` : ''}
              </div>
            </li>
          ))}
        </ul>

        {error && (
          <div className="mt-3 rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
            {error}
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button variant="secondary" full onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button full onClick={onCommit} disabled={isPending}>
            {isPending ? t('adjust.committing') : t('adjust.commitBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface SubmitSuccessProps {
  count: number;
  units: number;
  onStartAnother: () => void;
}

function SubmitSuccess({ count, units, onStartAnother }: SubmitSuccessProps) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-12 text-center">
      <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-success-bg text-success">
        <Icon name="check" size={36} strokeWidth={2.2} />
      </div>
      <h2 className="mt-5 text-[22px] font-semibold tracking-[-0.02em]">{t('adjust.adjusted')}</h2>
      <p className="mt-1 max-w-[280px] text-[13.5px] leading-[1.5] text-muted">
        {t('adjust.adjustedBody', {
          count,
          lineLabel: t('common.line', { count }),
          units,
          unitLabel: t('common.unit', { count: units }),
        })}
      </p>
      <div className="mt-6 flex w-full flex-col gap-2.5">
        <Button size="lg" full onClick={onStartAnother}>
          <Icon name="plus" size={18} /> {t('adjust.startAnother')}
        </Button>
        <Button asChild size="lg" full variant="ghost">
          <Link to="/">{t('common.backToHome')}</Link>
        </Button>
      </div>
    </div>
  );
}
